import { createSlice, nanoid } from "@reduxjs/toolkit";

const loadActive = () => {
  const saved = localStorage.getItem("activeChatId");
  // guests use temp ids; never restore them on startup (fresh chat on open/refresh)
  if (!saved || saved === "null" || saved === "undefined") return null;
  if (saved.startsWith("temp-")) return null;
  return saved;
};

const initialActive = loadActive();

const initialState = {
  chats: {},              // { chatId: { messages: [], loading:false, requestId:null } }
  activeChatId: initialActive,
  chatList: [],           // List of all user chats for sidebar
  sidebarCollapsed: typeof window !== "undefined" ? window.innerWidth <= 1024 : false, // Auto-collapse on mobile & tablet by default
};


// ================= ENSURE CHAT =================

const ensureChat = (state, chatId) => {
  if (!chatId) return;

  if (!state.chats[chatId]) {
    state.chats[chatId] = {
      messages: [],
      loading: false,
      requestId: null,
    };
  }
};


// ================= HYDRATE ACTIVE CHAT ON START =================

if (initialActive) {
  let initialMessages = [];

  // ignore temp chats (guests) on startup — they should start fresh
  if (initialActive.startsWith("temp-")) {
    initialMessages = [];
  } else {
    try {
      const saved = localStorage.getItem(`chat_messages_${initialActive}`);
      if (saved) initialMessages = JSON.parse(saved);
    } catch (e) {
      console.error("Failed to load messages", e);
    }
  }

  initialState.chats[initialActive] = {
    messages: initialMessages,
    loading: false,
    requestId: null,
  };
}


const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {

    // ================= CREATE LOCAL CHAT =================

    createLocalChat: {
      reducer: (state, action) => {
        const chatId = action.payload;

        ensureChat(state, chatId);

        state.activeChatId = chatId;
        localStorage.setItem("activeChatId", chatId);
      },
      prepare: () => ({
        payload: "local-" + nanoid(),
      }),
    },

    // ================= ACTIVE CHAT =================

    setActiveChatId: (state, action) => {
        const chatId = action.payload;

        ensureChat(state, chatId);

        state.activeChatId = chatId;

        // ✅ SAVE CHAT ID TO PERSIST SESSION
        if (chatId) {
            localStorage.setItem("activeChatId", chatId);
        }
    },

    clearActiveChat: (state) => {
      if (state.activeChatId?.startsWith("temp-")) {
        localStorage.removeItem(`chat_messages_${state.activeChatId}`);
      }
      state.activeChatId = null;
      localStorage.removeItem("activeChatId");
    },

    // ================= MIGRATION LOCAL → SERVER =================

    migrateChatId: (state, action) => {
      const { oldId, newId } = action.payload;

      if (!oldId || !newId || oldId === newId) return;
      if (!state.chats[oldId]) return;

      if (state.chats[newId]) {
        state.chats[newId].messages = [
          ...state.chats[oldId].messages,
          ...state.chats[newId].messages,
        ];
      } else {
        state.chats[newId] = state.chats[oldId];
      }

      delete state.chats[oldId];

      if (oldId.startsWith("temp-")) {
        localStorage.removeItem(`chat_messages_${oldId}`);
      }

      if (state.activeChatId === oldId) {
        state.activeChatId = newId;
        localStorage.setItem("activeChatId", newId);
      }
    },

    // ================= MESSAGES =================

    setMessages: (state, action) => {
      const { chatId, messages } = action.payload;

      ensureChat(state, chatId);

      const normalized = (messages || []).map((msg) => {
        const meta = msg.metadata || {};
        if (Array.isArray(meta.versions) && meta.versions.length > 0) {
          const vIdx = meta.versionIndex ?? (meta.versions.length - 1);
          return {
            ...msg,
            versions: meta.versions,
            versionIndex: vIdx,
            content: meta.versions[vIdx]?.content || msg.content,
            sources: meta.versions[vIdx]?.sources || msg.sources || meta.sources || []
          };
        }
        return msg;
      });

      state.chats[chatId].messages = normalized;

      if (chatId.startsWith("temp-")) {
        localStorage.setItem(`chat_messages_${chatId}`, JSON.stringify(normalized));
      }
    },

    addMessage: (state, action) => {
      const { chatId, message } = action.payload;

      ensureChat(state, chatId);

      state.chats[chatId].messages.push({
        id: nanoid(),   // prevent React key issues
        ...message,
      });

      // ✅ SAVE TEMP CHAT MESSAGES TO LOCALSTORAGE
      if (chatId.startsWith("temp-")) {
        localStorage.setItem(
          `chat_messages_${chatId}`,
          JSON.stringify(state.chats[chatId].messages)
        );
      }
    },

    updateMessageVersion: (state, action) => {
      const { chatId, messageIndex, versionIndex } = action.payload;
      ensureChat(state, chatId);
      const msg = state.chats[chatId]?.messages?.[messageIndex];
      if (msg) {
        const versions = Array.isArray(msg.versions) && msg.versions.length > 0
          ? msg.versions
          : (Array.isArray(msg.metadata?.versions) && msg.metadata.versions.length > 0 ? msg.metadata.versions : null);

        if (versions && versions[versionIndex]) {
          msg.versions = versions;
          msg.versionIndex = versionIndex;
          msg.content = versions[versionIndex].content;
          msg.sources = versions[versionIndex].sources || [];

          if (!msg.metadata) msg.metadata = {};
          msg.metadata.versions = versions;
          msg.metadata.versionIndex = versionIndex;
          msg.metadata.sources = msg.sources;

          if (chatId.startsWith("temp-")) {
            localStorage.setItem(`chat_messages_${chatId}`, JSON.stringify(state.chats[chatId].messages));
          }
        }
      }
    },

    appendMessageVersion: (state, action) => {
      const { chatId, messageIndex, newContent, newSources } = action.payload;
      ensureChat(state, chatId);
      const msg = state.chats[chatId]?.messages?.[messageIndex];
      if (msg) {
        let versions = Array.isArray(msg.versions) && msg.versions.length > 0
          ? [...msg.versions]
          : (Array.isArray(msg.metadata?.versions) && msg.metadata.versions.length > 0 ? [...msg.metadata.versions] : null);

        if (!versions || versions.length === 0) {
          versions = [{ content: msg.content, sources: msg.sources || [] }];
        }
        versions.push({
          content: newContent,
          sources: newSources || [],
          timestamp: new Date().toISOString()
        });

        msg.versions = versions;
        msg.versionIndex = versions.length - 1;
        msg.content = newContent;
        msg.sources = newSources || [];

        if (!msg.metadata) msg.metadata = {};
        msg.metadata.versions = versions;
        msg.metadata.versionIndex = msg.versionIndex;
        msg.metadata.sources = msg.sources;

        if (chatId.startsWith("temp-")) {
          localStorage.setItem(`chat_messages_${chatId}`, JSON.stringify(state.chats[chatId].messages));
        }
      }
    },

    // ================= LOADING =================

    setLoading: (state, action) => {
      const { chatId, value } = action.payload;

      ensureChat(state, chatId);

      state.chats[chatId].loading = value;
    },

    // ================= REQUEST ISOLATION =================

    setRequestId: (state, action) => {
      const { chatId, requestId } = action.payload;

      ensureChat(state, chatId);

      state.chats[chatId].requestId = requestId;
    },

    // ================= TYPING =================

    updateMessageTyping: (state, action) => {
      const { chatId, index } = action.payload;

      if (state.chats[chatId]?.messages[index]) {
        state.chats[chatId].messages[index].typing = false;
      }
    },

    // ================= CHAT LIST MANAGEMENT =================

    setChatList: (state, action) => {
      state.chatList = action.payload || [];
    },

    addChatToList: (state, action) => {
      const newChat = action.payload;
      // Add to beginning of list
      state.chatList = [newChat, ...state.chatList];
    },

    removeChatFromList: (state, action) => {
      const chatId = action.payload;
      state.chatList = state.chatList.filter(c => c._id !== chatId);
    },

    // ================= SIDEBAR MANAGEMENT =================

    setSidebarCollapsed: (state, action) => {
      state.sidebarCollapsed = action.payload;
    },

    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },

  },
});


export const {
  createLocalChat,
  setActiveChatId,
  clearActiveChat,
  migrateChatId,
  setMessages,
  addMessage,
  setLoading,
  setRequestId,
  updateMessageTyping,
  setChatList,
  addChatToList,
  removeChatFromList,
  setSidebarCollapsed,
  toggleSidebarCollapsed,
  updateMessageVersion,
  appendMessageVersion,
} = chatSlice.actions;

export default chatSlice.reducer;